import { loadJobPhotos, addJobPhoto, removeJobPhoto } from '../services/jobPhotos.js';
import { isAdmin } from '../services/adminAuth.js';
import { prepareImageForUpload } from '../services/imagePrep.js';
import { generateInvoicePdf } from '../services/invoicePdf.js';
import { parseLocalDate } from '../services/dates.js';
import { db } from '../services/firebase.js';
import { normaliseReviewUrl, reviewUrlError } from '../services/reviewLink.js';
import { RevenueService, RevenueMixin } from '../services/revenue.js';
import { loadBillboard } from '../vendor.js';

export const UserProfile = {
  name: "UserProfile",
  inject: ["user", "isAuthenticated", "handleLogout", "isLoading"],
  mixins: [RevenueMixin], // Add consistent revenue handling
  data() {
    return {
      userJobs: [],
      profileLoading: true,
      selectedTimespan: "week",
      chart: null,
      profileData: {
        email: "",
        companyName: "",
        userName: "",
        websiteURL: "",
        instagramURL: "",
        twitterURL: "",
        reviewUrl: ""
      },

      // Business profile editing. Until now these fields were written once at
      // registration and never again - there was no form anywhere in the app.
      showProfileSettings: false,
      profileForm: {
        companyName: "",
        websiteURL: "",
        instagramURL: "",
        reviewUrl: ""
      },
      profileSaving: false,
      profileSaveError: null,
      profileSaved: false,

      // Review-ask outcomes for this operator's own jobs.
      reviewStats: { sent: 0, clicked: 0 },
      searchQuery: "",
      selectedService: "",
      dateRange: {
        start: "",
        end: ""
      },
      showAdvancedFilters: false,
      isAdmin: false,
      viewingUserId: null,
      selectedJob: null,
      jobModal: null,
      statusColors: {
        scheduled: "bg-primary",
        completed: "bg-success",
        cancelled: "bg-danger",
        pending: "bg-warning",
        inProgress: "bg-info"
      },
      selectedStatus: "scheduled",
      jobStatuses: [
        {
          value: "pending",
          label: "Unscheduled",
          variant: "warning",
          icon: "bi bi-clock-fill",
          description: "Awaiting scheduling"
        },
        {
          value: "scheduled",
          label: "Scheduled",
          variant: "primary",
          icon: "bi bi-calendar-check-fill",
          description: "Date and time confirmed"
        },
        {
          value: "inProgress",
          label: "In Progress",
          variant: "info",
          icon: "bi bi-gear-fill",
          description: "Work being performed"
        },
        {
          value: "completed",
          label: "Completed",
          variant: "success",
          icon: "bi bi-check-circle-fill",
          description: "Job finished"
        },
        {
          value: "cancelled",
          label: "Cancelled",
          variant: "danger",
          icon: "bi bi-x-circle-fill",
          description: "Job cancelled"
        }
      ],
      statusFilters: {
        upcoming: ["scheduled", "pending"],
        active: ["inProgress"],
        finished: ["completed", "cancelled"]
      },
      isGeneratingPDF: false,
      isSending: false,
      showInvoiceContent: false,
      // Edit customer contact in job details
      editMode: false,
      editForm: {
        customerName: "",
        customerPhone: "",
        customerEmail: "",
        serviceLocation: "",
        saving: false,
        errors: {
          customerName: "",
          customerPhone: "",
          customerEmail: "",
          serviceLocation: ""
        }
      },
      originalCustomerData: null,
      // Image upload
      photoUpload: {
        uploading: false,
        deleting: false,
        progress: 0,
        currentType: null // 'before' or 'after'
      },
      photoModal: {
        url: "",
        title: "",
        date: "",
        type: "",
        index: -1
      },
      searchTrends: {
        loading: false,
        error: null,
        lastUpdated: null,
        data: {
          pressureWashing: {
            currentVolume: 0,
            change: 0,
            trend: 'stable' // 'up', 'down', 'stable'
          },
          topKeywords: [],
          competitorAnalysis: {
            avgPosition: 0,
            opportunities: 0
          },
          seasonalInsights: {
            peakMonths: [],
            currentSeason: 'normal'
          }
        },
        selectedPeriod: '30d' // '7d', '30d', '90d'
      }
    };
  },
  computed: {
    displayName() {
      return (
        this.profileData.companyName ||
        this.profileData?.email?.split("@")[0] ||
        "User"
      );
    },

    displayInitial() {
      return this.displayName[0].toUpperCase();
    },

    // Null when the link is fine or simply empty; otherwise the message shown
    // under the field. The server re-checks this at send time regardless.
    reviewUrlProblem() {
      return reviewUrlError(this.profileForm.reviewUrl);
    },

    canSaveProfile() {
      return !this.profileSaving && !this.reviewUrlProblem;
    },

    // Updated job stats using consistent revenue calculation
    jobStats() {
      if (!this.userJobs.length) {
        return {
          totalJobs: 0,
          totalRevenue: '$0.00',
          averageJobAmount: '$0.00',
          mostCommonService: 'N/A',
          lastSubmission: null
        };
      }

      return {
        totalJobs: this.userJobs.length,
        // Use ONLY paid invoice revenue (consistent across app)
        totalRevenue: this.formattedRevenue.actual,
        averageJobAmount: this.formattedRevenue.average,
        mostCommonService: this.getMostCommonService(),
        lastSubmission: this.userJobs[0]?.dateSubmitted,
        // Additional revenue insights
        pendingRevenue: this.formattedRevenue.pending,
        conversionRate: Math.round(this.revenueMetrics.conversionRate)
      };
    },

    chartData() {
      if (!this.userJobs.length) return { dates: [], revenues: [] };

      const parsePrice = (price) => parseFloat(price.replace(/[$,]/g, "")) || 0;

      // Fix date parsing to handle MM/DD/YYYY format
      const parseDate = (dateStr, timeStr) => {
        const [month, day, year] = dateStr.split("/").map(Number);
        const [hours, minutes, seconds] = timeStr.split(":");
        const period = seconds.split(" ")[1];
        let hour = parseInt(hours);
        if (period === "PM" && hour !== 12) hour += 12;
        if (period === "AM" && hour === 12) hour = 0;
        return new Date(year, month - 1, day, hour, parseInt(minutes));
      };

      const jobsWithDates = this.userJobs.map((job) => ({
        date: parseDate(job.dateSubmitted, job.timeSubmitted),
        revenue: parsePrice(job.quotedPrice)
      }));

      // Sort by date
      jobsWithDates.sort((a, b) => a.date - b.date);

      // Get date range
      const startDate = new Date(
        Math.min(...jobsWithDates.map((job) => job.date))
      );
      const endDate = new Date(
        Math.max(...jobsWithDates.map((job) => job.date))
      );

      switch (this.selectedTimespan) {
        case "daily": {
          try {
            // Generate all dates between start and end
            const dates = [];
            const currentDate = new Date(startDate);
            currentDate.setHours(0, 0, 0, 0);
            const endOfLastDay = new Date(endDate);
            endOfLastDay.setHours(23, 59, 59, 999);

            while (currentDate <= endOfLastDay) {
              dates.push(new Date(currentDate));
              currentDate.setDate(currentDate.getDate() + 1);
            }

            // Calculate daily revenue
            const dailyData = dates.map((date) => {
              const dayRevenue = jobsWithDates
                .filter((job) => {
                  const jobDate = new Date(job.date);
                  return (
                    jobDate.getFullYear() === date.getFullYear() &&
                    jobDate.getMonth() === date.getMonth() &&
                    jobDate.getDate() === date.getDate()
                  );
                })
                .reduce((sum, job) => sum + job.revenue, 0);

              return {
                date: date.toISOString().split("T")[0],
                revenue: dayRevenue
              };
            });

            return {
              dates: dailyData.map((d) => d.date),
              revenues: dailyData.map((d) => d.revenue)
            };
          } catch (error) {
            console.error("Error calculating daily revenue:", error);
            return {
              dates: [],
              revenues: []
            };
          }
        }
        case "yearly": {
          const years = [];
          let currentYear = startDate.getFullYear();
          const endYear = endDate.getFullYear();

          while (currentYear <= endYear) {
            years.push(currentYear.toString());
            currentYear++;
          }

          const yearlyData = years.map((year) => {
            const yearRevenue = jobsWithDates
              .filter((job) => job.date.getFullYear().toString() === year)
              .reduce((sum, job) => sum + job.revenue, 0);

            return {
              date: year,
              revenue: yearRevenue
            };
          });

          return {
            dates: yearlyData.map((d) => d.date),
            revenues: yearlyData.map((d) => d.revenue)
          };
        }
        case "monthly":
        default: {
          // Generate all months between start and end
          const months = [];
          const currentDate = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            1
          );
          const endMonth = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            1
          );

          while (currentDate <= endMonth) {
            months.push(new Date(currentDate));
            currentDate.setMonth(currentDate.getMonth() + 1);
          }

          const monthlyData = months.map((date) => {
            const monthRevenue = jobsWithDates
              .filter(
                (job) =>
                  job.date.getFullYear() === date.getFullYear() &&
                  job.date.getMonth() === date.getMonth()
              )
              .reduce((sum, job) => sum + job.revenue, 0);

            return {
              date: date.toISOString().slice(0, 7),
              revenue: monthRevenue
            };
          });

          return {
            dates: monthlyData.map((d) => d.date),
            revenues: monthlyData.map((d) => d.revenue)
          };
        }
      }
    }, // New: Compute chart data based on selected timespan

    uniqueServices() {
      return [...new Set(this.userJobs.map((job) => job.serviceType))];
    },

    // hasActiveFilters() {
    //   return (
    //     this.searchQuery ||
    //     this.selectedService ||
    //     this.dateRange.start ||
    //     this.dateRange.end
    //   );
    // },

    //     filteredJobs() {
    //       return this.userJobs.filter((job) => {
    //         // Search query filter
    //         const searchLower = this.searchQuery.toLowerCase();
    //         const matchesSearch =
    //           !this.searchQuery ||
    //           job.customerName.toLowerCase().includes(searchLower) ||
    //           job.serviceLocation.toLowerCase().includes(searchLower) ||
    //           job.serviceType.toLowerCase().includes(searchLower);

    //         // Service type filter
    //         const matchesService =
    //           !this.selectedService || job.serviceType === this.selectedService;

    //         // Date range filter
    //         const jobDate = new Date(job.dateSubmitted);
    //         const matchesDateRange =
    //           (!this.dateRange.start ||
    //             jobDate >= new Date(this.dateRange.start)) &&
    //           (!this.dateRange.end || jobDate <= new Date(this.dateRange.end));

    //         return matchesSearch && matchesService && matchesDateRange;
    //       });
    //     },

    targetUserId() {
      // Use route param if it exists (admin viewing someone), otherwise use current user
      return this.$route.params.id || this.user?.uid;
    },

    isAdminView() {
      return this.$route.params.id && this.isAdmin;
    },

    hasActiveFilters() {
      return (
        this.searchQuery ||
        this.selectedService ||
        this.selectedStatus ||
        this.dateRange.start ||
        this.dateRange.end
      );
    },

    filteredJobs() {
      return this.userJobs.filter((job) => {
        const currentStatus = this.determineJobStatus(job);

        // Search query filter
        const searchLower = this.searchQuery.toLowerCase();
        const matchesSearch =
          !this.searchQuery ||
          job.customerName.toLowerCase().includes(searchLower) ||
          job.serviceLocation.toLowerCase().includes(searchLower) ||
          job.serviceType.toLowerCase().includes(searchLower);

        // Service type filter
        const matchesService =
          !this.selectedService || job.serviceType === this.selectedService;

        // Status filter
        const matchesStatus =
          !this.selectedStatus || currentStatus === this.selectedStatus;

        // Date range filter
        const jobDate = new Date(job.dateSubmitted);
        const matchesDateRange =
          (!this.dateRange.start ||
            jobDate >= new Date(this.dateRange.start)) &&
          (!this.dateRange.end || jobDate <= new Date(this.dateRange.end));

        return (
          matchesSearch && matchesService && matchesStatus && matchesDateRange
        );
      });
    },

    filterStats() {
      const stats = {
        total: this.userJobs.length,
        filtered: this.filteredJobs.length,
        byStatus: {}
      };

      this.jobStatuses.forEach((status) => {
        stats.byStatus[status.value] = this.userJobs.filter(
          (job) => job.status === status.value
        ).length;
      });

      return stats;
    },

    jobsByStatus() {
      const grouped = {
        pending: [],
        scheduled: [],
        inProgress: [],
        completed: [],
        cancelled: []
      };

      this.userJobs.forEach((job) => {
        const status = this.determineJobStatus(job);
        if (grouped[status]) {
          grouped[status].push(job);
        }
      });

      return grouped;
    },
    
    averageJobAmount() {
      if (!this.userJobs.length) return '$0.00';

      const totalRevenue = this.userJobs.reduce((total, job) => {
        const price = parseFloat(job.quotedPrice.replace(/[$,]/g, '')) || 0;
        return total + price;
      }, 0);

      const average = totalRevenue / this.userJobs.length;

      return average.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
  },
  methods: {
    getMostCommonService() {
      if (!this.userJobs.length) return 'N/A';

      const serviceTypes = this.userJobs.reduce((acc, job) => {
        if (job.serviceType) {
          acc[job.serviceType] = (acc[job.serviceType] || 0) + 1;
        }
        return acc;
      }, {});

      return Object.entries(serviceTypes).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A";
    },

    // One indexed query scoped to this operator, so the click tracking the
    // server records is actually visible to the person it is for.
    async fetchReviewStats() {
      if (!this.targetUserId) return;
      try {
        const snap = await db
          .ref("reviewRequests")
          .orderByChild("userId")
          .equalTo(this.targetUserId)
          .once("value");
        let sent = 0;
        let clicked = 0;
        snap.forEach((child) => {
          const request = child.val() || {};
          if (request.sentAt) sent++;
          if (request.clickedAt) clicked++;
        });
        this.reviewStats = { sent, clicked };
      } catch (error) {
        // Never let a stats read break the dashboard.
        console.warn("Could not load review stats:", error.message);
      }
    },

    toggleProfileSettings() {
      this.showProfileSettings = !this.showProfileSettings;
      if (this.showProfileSettings) {
        // Edit a copy, so cancelling leaves the displayed profile untouched.
        this.profileForm = {
          companyName: this.profileData.companyName || "",
          websiteURL: this.profileData.websiteURL || "",
          instagramURL: this.profileData.instagramURL || "",
          reviewUrl: this.profileData.reviewUrl || ""
        };
        this.profileSaveError = null;
        this.profileSaved = false;
      }
    },

    async saveProfile() {
      if (!this.canSaveProfile) return;
      this.profileSaving = true;
      this.profileSaveError = null;
      try {
        // Store the normalised form, not the raw paste: a trailing space or a
        // missing scheme would otherwise reach a customer's invoice.
        const patch = {
          companyName: this.profileForm.companyName.trim(),
          websiteURL: this.profileForm.websiteURL.trim(),
          instagramURL: this.profileForm.instagramURL.trim(),
          reviewUrl: normaliseReviewUrl(this.profileForm.reviewUrl) || ""
        };
        await db.ref(`users/${this.targetUserId}`).update(patch);
        this.profileData = { ...this.profileData, ...patch };
        this.profileSaved = true;
        this.showProfileSettings = false;
      } catch (error) {
        console.error("Could not save the business profile:", error);
        this.profileSaveError = error.message || "Could not save. Try again.";
      } finally {
        this.profileSaving = false;
      }
    },

    testReviewLink() {
      const url = normaliseReviewUrl(this.profileForm.reviewUrl);
      if (url) window.open(url, "_blank", "noopener");
    },

    async fetchUserData() {
  try {
    if (!this.targetUserId) {
      console.log("No user UID found");
      this.profileLoading = false;
      return;
    }

    // First, check admin status
    await this.checkAdminStatus();

    // Then, fetch user profile data - update the reference
    const userRef = db.ref(`users/${this.targetUserId}`);
    const userSnapshot = await userRef.get();

    if (userSnapshot.exists()) {
      const userData = userSnapshot.val();
      this.profileData = {
        email: userData.email || this.user.email,
        companyName: userData.companyName || "",
        userName: userData.userName || "",
        websiteURL: userData.websiteURL || "",
        instagramURL: userData.instagramURL || "",
        twitterURL: userData.twitterURL || "",
        reviewUrl: userData.reviewUrl || ""
      };
    }

    // Then fetch jobs
    const jobsRef = db.ref("jobs");
    const jobsQuery = jobsRef
      .orderByChild("userId")
      .equalTo(this.targetUserId);
    const jobsSnapshot = await jobsQuery.get();

    if (jobsSnapshot.exists()) {
      const jobs = [];
      jobsSnapshot.forEach((childSnapshot) => {
        const job = childSnapshot.val();
        job.id = childSnapshot.key;
        
        // Photos no longer travel with the jobs list; kept for any record not
        // yet migrated out of jobs/{id}/photos.
        if (job.photos) {
          ['before', 'after'].forEach(type => {
            if (job.photos[type]) {
              // Convert Firebase pseudo-arrays to real arrays
              job.photos[type] = Object.values(job.photos[type]);
            }
          });
        }
        
        jobs.push(job);
      });

      this.userJobs = jobs.sort((a, b) => {
        const dateA = new Date(`${a.dateSubmitted} ${a.timeSubmitted}`);
        const dateB = new Date(`${b.dateSubmitted} ${b.timeSubmitted}`);
        return dateB - dateA;
      });

      if (this.userJobs.length > 0) {
        // Calculate ONLY invoiced revenue (to match Invoice component)
        const invoicedRevenue = this.userJobs.reduce((total, job) => {
          // Only count jobs that have invoices
          if (job.invoices && Object.keys(job.invoices).length > 0) {
            const price = parseFloat(job.quotedPrice.replace(/[$,]/g, "")) || 0;
            return total + price;
          }
          return total;
        }, 0);

        // Calculate average job amount (based on invoiced jobs only)
        const invoicedJobs = this.userJobs.filter(job => job.invoices && Object.keys(job.invoices).length > 0);
        const averageAmount = invoicedJobs.length > 0 ? invoicedRevenue / invoicedJobs.length : 0;

        // Count service types
        const serviceTypes = this.userJobs.reduce((acc, job) => {
          if (job.serviceType) {
            acc[job.serviceType] = (acc[job.serviceType] || 0) + 1;
          }
          return acc;
        }, {});

        // Update job statistics
        this.jobStats = {
          totalJobs: this.userJobs.length,
          totalRevenue: invoicedRevenue.toLocaleString("en-US", {
            style: "currency",
            currency: "USD"
          }),
          averageJobAmount: averageAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
          }),
          mostCommonService: Object.entries(serviceTypes).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A",
          lastSubmission: this.userJobs[0].dateSubmitted
        };
      }
    }
  } catch (error) {
    console.error("Error in fetchUserData:", error);
  } finally {
    this.profileLoading = false;
  }
},

    async initChart() {
      if (this.chart) {
        this.chart.destroy();
      }

      const { dates, revenues } = this.chartData;

      if (!dates.length) return;

      const bb = await loadBillboard();
      this.chart = bb.generate({
        bindto: "#revenueChart",
        data: {
          x: "x",
          columns: [
            ["x", ...dates],
            ["Revenue", ...revenues]
          ],
          type: "spline"
        },
        axis: {
          x: {
            type: "timeseries",
            tick: {
              format:
                this.selectedTimespan === "yearly"
                  ? "%Y"
                  : this.selectedTimespan === "monthly"
                  ? "%Y-%m"
                  : "%m-%d",
              rotate: 45,
              multiline: false
            }
          },
          y: {
            tick: {
              format: function (x) {
                return "$" + x.toLocaleString();
              }
            }
          }
        },
        point: {
          show: false
        },
        grid: {
          y: {
            show: false
          }
        }
      });
    },

    updateTimespan(span) {
      this.selectedTimespan = span;
      this.$nextTick(() => {
        this.initChart();
      });
    },

    clearFilters() {
      this.searchQuery = "";
      this.selectedService = "";
      this.dateRange.start = "";
      this.dateRange.end = "";
      this.showAdvancedFilters = false;
    },

    formatDate(dateString) {
      const date = parseLocalDate(dateString);
      return date ? date.toLocaleDateString() : "";
    },

    async checkAdminStatus() {
      try {
        const user = firebase.auth().currentUser;
        if (!user) return;

        this.isAdmin = await isAdmin();
      } catch (err) {
        console.error("Error checking admin status:", err);
        this.isAdmin = false;
      }
    },

    // openJobDetails(job) {
    //   this.selectedJob = job;
    //   if (!this.jobModal) {
    //     this.jobModal = new bootstrap.Modal('#jobDetailsModal');
    //   }
    //   this.jobModal.show();
    // },

    formatStatus(status) {
      if (!status) return "Pending";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    getStatusBadgeClass(status) {
      const baseClasses = "badge";
      const statusClass = this.statusColors[status || "pending"];
      return `${baseClasses} ${statusClass}`;
    },

    formatScheduledDateTime(job) {
      if (!job.scheduledDate) return "";

      // Directly format the date to avoid timezone issues
      let dateStr;

      // Handle different date formats
      if (
        typeof job.scheduledDate === "string" &&
        job.scheduledDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        // Parse YYYY-MM-DD format directly
        const [year, month, day] = job.scheduledDate.split("-").map(Number);
        const localDate = new Date(year, month - 1, day);
        dateStr = localDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric"
        });
      } else {
        // Use the existing formatDate method as fallback
        dateStr = this.formatDate(job.scheduledDate);
      }

      // Add time if available
      if (job.scheduledTime) {
        dateStr += ` at ${this.formatTime(job.scheduledTime)}`;
      }

      return dateStr;
    },

    formatTime(timeStr) {
      if (!timeStr) return "";
      const [hours, minutes] = timeStr.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    },

    // scheduleJob(job) {
    //   // Navigate to scheduler with job ID
    //   this.$router.push({
    //     name: 'jobScheduler',
    //     query: { jobId: job.id }
    //   });
    // },

    quickFilterStatus(status) {
      if (this.selectedStatus === status) {
        this.selectedStatus = ""; // Toggle off if already selected
      } else {
        this.selectedStatus = status;
      }
    },

    getStatusCount(status) {
      return this.userJobs.filter((job) => job.status === status).length;
    },

    getStatusLabel(statusValue) {
      const status = this.jobStatuses.find((s) => s.value === statusValue);
      return status ? status.label : statusValue;
    },

    clearDateRange() {
      this.dateRange.start = "";
      this.dateRange.end = "";
    },

    clearFilters() {
      this.searchQuery = "";
      this.selectedService = "";
      this.selectedStatus = "";
      this.dateRange.start = "";
      this.dateRange.end = "";
      this.showAdvancedFilters = false;
    },

    //     // Initialize default status for new jobs
    //     setDefaultJobStatus(job) {
    //       if (!job.status) {
    //         if (job.scheduledDate && job.scheduledTime) {
    //           return 'scheduled';
    //         }
    //         return 'pending';
    //       }
    //       return job.status;
    //     },

    //     // Update jobs with status if missing
    //     initializeJobStatuses() {
    //       this.userJobs = this.userJobs.map(job => ({
    //         ...job,
    //         status: this.setDefaultJobStatus(job)
    //       }));
    //     },

    determineJobStatus(job) {
      // If the job has an explicit status set (e.g., completed or cancelled)
      if (job.status === "completed" || job.status === "cancelled") {
        return job.status;
      }

      // Check if job is scheduled
      if (job.scheduledDate && job.scheduledTime) {
        const scheduledDateTime = new Date(
          `${job.scheduledDate} ${job.scheduledTime}`
        );
        const now = new Date();

        // If the scheduled time is in the future
        if (scheduledDateTime > now) {
          return "scheduled";
        }

        // If the scheduled time has passed but not marked complete
        if (scheduledDateTime <= now) {
          return "inProgress";
        }
      }

      // Default to pending if no schedule is set
      return "pending";
    },

    // Method to update job status in database
    async updateJobStatus(jobId, newStatus) {
      try {
        await firebase.database().ref(`jobs/${jobId}`).update({
          status: newStatus,
          lastUpdated: firebase.database.ServerValue.TIMESTAMP
        });

        // If status is completed, update completion timestamp
        if (newStatus === "completed") {
          await firebase.database().ref(`jobs/${jobId}`).update({
            completedAt: firebase.database.ServerValue.TIMESTAMP
          });
        }

        // Refresh job data
        await this.fetchUserData();
      } catch (error) {
        console.error("Error updating job status:", error);
      }
    },

    // Method to get status count including automatic status determination
    getStatusCount(status) {
      return this.userJobs.filter(
        (job) => this.determineJobStatus(job) === status
      ).length;
    },

    // Updated method for scheduling a job
    async scheduleJob(job) {
      if (this.determineJobStatus(job) === "pending") {
        this.$router.push({
          name: "jobScheduler",
          query: { 
            jobId: job.id,
            openModal: 'true'
          }
        });
      }
    },

    // Method to get allowed status transitions
    getAllowedStatusTransitions(currentStatus) {
      const transitions = {
        pending: ["scheduled", "cancelled"],
        scheduled: ["inProgress", "cancelled"],
        inProgress: ["completed", "cancelled"],
        completed: [], // No transitions allowed from completed
        cancelled: ["pending"] // Allow reactivating cancelled jobs
      };

      return transitions[currentStatus] || [];
    },

    // Updated method for status badge classes
    getStatusBadgeClass(job) {
      const status = this.determineJobStatus(job);
      const statusObj = this.jobStatuses.find((s) => s.value === status);

      return {
        badge: true,
        [`alert-${statusObj?.variant || "secondary"}`]: true,
        "text-white": false,
        "position-relative": status === "inProgress"
      };
    },

    // Helper to format the time until scheduled date
    getTimeUntilScheduled(job) {
      if (!job.scheduledDate || !job.scheduledTime) return "";

      const scheduledDateTime = new Date(
        `${job.scheduledDate} ${job.scheduledTime}`
      );
      const now = new Date();
      const diffTime = Math.abs(scheduledDateTime - now);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(
        (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );

      if (scheduledDateTime < now) {
        return "In Progress";
      }

      if (diffDays > 0) {
        return `Starts in ${diffDays}d ${diffHours}h`;
      }

      return `Starts in ${diffHours}h`;
    },

    // Delegates to the shared generator; see src/services/invoicePdf.js.
    async generatePDF(job) {
      const invoice = (job && job.invoices)
        ? Object.values(job.invoices).filter(Boolean).sort(
            (a, b) => (b.sentDate || 0) - (a.sentDate || 0)
          )[0] || {}
        : {};
      const { doc } = await generateInvoicePdf({
        job: job || {},
        invoice,
        uid: this.user && this.user.uid,
      });
      return doc;
    },

    async sendInvoiceEmail(job) {
      this.isSending = true;
      let pdfDoc = null;

      try {
        // Get current authenticated user
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          throw new Error("No authenticated user found");
        }

        // Generate PDF with error handling
        console.log("Generating PDF...");
        pdfDoc = await this.generatePDF(job);

        if (!pdfDoc) {
          throw new Error("PDF generation failed");
        }

        console.log("Converting PDF to base64...");
        const pdfBase64 = pdfDoc.output("datauristring");

        if (!pdfBase64) {
          throw new Error("PDF to base64 conversion failed");
        }

        const base64Content = pdfBase64.split(",")[1];

        if (!base64Content) {
          throw new Error("Invalid PDF base64 content");
        }

        console.log("Preparing payload for cloud function...");
        const payload = {
          pdfBase64: base64Content,
          jobId: job.id,
          customerEmail: job.customerEmail,
          customerName: job.customerName,
          invoiceNumber: job.id.slice(-6).toUpperCase(),
          userId: currentUser.uid,
          vendor: job.vendor || this.profileData?.companyName || "Company Name"
        };

        // Validate payload
        const requiredFields = [
          "customerEmail",
          "customerName",
          "invoiceNumber",
          "userId"
        ];
        const missingFields = requiredFields.filter((field) => !payload[field]);

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }

        console.log("Sending to cloud function...");
        const sendInvoiceEmail = firebase
          .functions()
          .httpsCallable("sendInvoiceEmail");
        const result = await sendInvoiceEmail(payload);

        if (!result.data?.success) {
          throw new Error(result.data?.error || "Failed to send invoice");
        }

        // Create standardized invoice with complete data
        const invoiceResult = await RevenueService.createInvoice(job, currentUser.uid, 'sent');
        if (!invoiceResult.success) {
          throw new Error(`Failed to create invoice: ${invoiceResult.error}`);
        }

        // Success!
        alert("Invoice sent successfully!"); // replace this with message component
        return { success: true, message: "Invoice sent successfully!" };
      } catch (error) {
        alert("Error sending email"); // replace this with message component
        console.error("Email Sending Error:", error);
        throw error;
      } finally {
        this.isSending = false;
        // Clean up PDF document if needed
        if (pdfDoc && typeof pdfDoc.destroy === "function") {
          pdfDoc.destroy();
        }
      }
    },

    // Edit functionality for job details

    toggleEditMode() {
      if (this.editMode) {
        // Currently in edit mode, trying to save
        this.saveCustomerInfo();
      } else {
        // Entering edit mode
        this.enterEditMode();
      }
    },

    enterEditMode() {
      if (!this.selectedJob) return;

      // Store original data for cancellation
      this.originalCustomerData = {
        customerName: this.selectedJob.customerName,
        customerPhone: this.selectedJob.customerPhone,
        customerEmail: this.selectedJob.customerEmail,
        serviceLocation: this.selectedJob.serviceLocation
      };

      // Populate edit form with current data
      this.editForm.customerName = this.selectedJob.customerName || "";
      this.editForm.customerPhone = this.selectedJob.customerPhone || "";
      this.editForm.customerEmail = this.selectedJob.customerEmail || "";
      this.editForm.serviceLocation = this.selectedJob.serviceLocation || "";

      // Clear any previous errors
      this.clearEditErrors();

      this.editMode = true;
    },

    cancelEdit() {
      // Restore original data
      if (this.originalCustomerData) {
        this.editForm.customerName = this.originalCustomerData.customerName;
        this.editForm.customerPhone = this.originalCustomerData.customerPhone;
        this.editForm.customerEmail = this.originalCustomerData.customerEmail;
        this.editForm.serviceLocation = this.originalCustomerData.serviceLocation;
      }

      this.clearEditErrors();
      this.editMode = false;
      this.originalCustomerData = null;
    },

    async saveCustomerInfo() {
      if (!this.selectedJob) return;

      // Validate all fields
      const isValid = this.validateAllCustomerFields();

      if (!isValid) {
        return; // Don't save if validation fails
      }

      this.editForm.saving = true;

      try {
        // Prepare update data
        const updates = {
          customerName: this.editForm.customerName.trim().toUpperCase(),
          customerPhone: this.formatPhoneNumber(
            this.editForm.customerPhone.trim()
          ),
          customerEmail: this.editForm.customerEmail.trim().toLowerCase(),
          serviceLocation: this.editForm.serviceLocation.trim(),
          lastUpdated: firebase.database.ServerValue.TIMESTAMP
        };

        // Update in Firebase
        await firebase
          .database()
          .ref(`jobs/${this.selectedJob.id}`)
          .update(updates);

        // Update local selectedJob data
        Object.assign(this.selectedJob, updates);

        // Exit edit mode
        this.editMode = false;
        this.originalCustomerData = null;
        this.clearEditErrors();

        // Refresh jobs data if you have this method
        if (this.fetchJobs) {
          await this.fetchJobs();
        }
      } catch (error) {
        console.error("Error updating customer information:", error);
        alert("Error saving changes. Please try again.");
      } finally {
        this.editForm.saving = false;
      }
    },

    validateAllCustomerFields() {
      const nameValid = this.validateCustomerName();
      const phoneValid = this.validateCustomerPhone();
      const emailValid = this.validateCustomerEmail();
      const locationValid = this.validateServiceLocation();

      return nameValid && phoneValid && emailValid && locationValid;
    },

    validateCustomerName() {
      this.editForm.errors.customerName = "";

      if (
        !this.editForm.customerName ||
        this.editForm.customerName.trim() === ""
      ) {
        this.editForm.errors.customerName = "Name is required";
        return false;
      }

      if (this.editForm.customerName.trim().length < 2) {
        this.editForm.errors.customerName =
          "Name must be at least 2 characters long";
        return false;
      }

      return true;
    },

    validateCustomerPhone() {
      this.editForm.errors.customerPhone = "";

      if (
        !this.editForm.customerPhone ||
        this.editForm.customerPhone.trim() === ""
      ) {
        this.editForm.errors.customerPhone = "Phone number is required";
        return false;
      }

      // Remove all non-digits to validate
      const digitsOnly = this.editForm.customerPhone.replace(/\D/g, "");

      if (digitsOnly.length !== 10) {
        this.editForm.errors.customerPhone = "Phone number must be 10 digits";
        return false;
      }

      return true;
    },

    validateCustomerEmail() {
      this.editForm.errors.customerEmail = "";

      if (
        !this.editForm.customerEmail ||
        this.editForm.customerEmail.trim() === ""
      ) {
        this.editForm.errors.customerEmail = "Email is required";
        return false;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (!emailRegex.test(this.editForm.customerEmail.trim())) {
        this.editForm.errors.customerEmail = "Invalid email address";
        return false;
      }

      return true;
    },

    validateServiceLocation() {
      this.editForm.errors.serviceLocation = "";

      if (
        !this.editForm.serviceLocation ||
        this.editForm.serviceLocation.trim() === ""
      ) {
        this.editForm.errors.serviceLocation = "Service location is required";
        return false;
      }

      if (this.editForm.serviceLocation.trim().length < 5) {
        this.editForm.errors.serviceLocation =
          "Please provide a complete address";
        return false;
      }

      return true;
    },

    formatPhoneInput() {
      // Real-time formatting as user types
      let value = this.editForm.customerPhone.replace(/\D/g, ""); // Remove non-digits

      if (value.length >= 6) {
        value = value.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      } else if (value.length >= 3) {
        value = value.replace(/(\d{3})(\d{0,3})/, "($1) $2");
      }

      this.editForm.customerPhone = value;
    },

    formatPhoneNumber(phone) {
      // Format phone number for storage (same as your Home component)
      const digitsOnly = phone.replace(/\D/g, "");

      if (digitsOnly.length === 10) {
        return digitsOnly.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      }

      return phone; // Return as-is if not 10 digits
    },

    clearEditErrors() {
      this.editForm.errors = {
        customerName: "",
        customerPhone: "",
        customerEmail: "",
        serviceLocation: ""
      };
    },

    // Add this method to handle opening the modal with edit mode reset
    async openJobDetailsModal(job) {
      this.selectedJob = job;

      // Photos are fetched here rather than arriving with the jobs list. They
      // were 57.8% of the bytes in every job read and are only ever shown in
      // this modal.
      this.selectedJob.photos = { before: [], after: [] };
      try {
        this.selectedJob.photos = await loadJobPhotos(job.id);
      } catch (error) {
        console.error('Could not load photos for this job:', error);
      }

      // Reset edit mode when opening modal
      this.editMode = false;
      this.originalCustomerData = null;
      this.clearEditErrors();

      // Show the modal using Bootstrap's modal API
      const modalElement = document.getElementById("jobDetailsModal");
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    },

    // Method to handle unsaved changes check when modal closes
    handleModalClose() {
      if (this.editMode) {
        const hasChanges = this.hasUnsavedChanges();

        if (hasChanges) {
          const confirmClose = confirm(
            "You have unsaved changes. Are you sure you want to close without saving?"
          );
          if (!confirmClose) {
            return false; // Prevent modal from closing
          }
        }

        this.cancelEdit(); // Reset edit state
      }

      return true; // Allow modal to close
    },

    hasUnsavedChanges() {
      if (!this.originalCustomerData) return false;

      return (
        this.editForm.customerName !== this.originalCustomerData.customerName ||
        this.editForm.customerPhone !==
          this.originalCustomerData.customerPhone ||
        this.editForm.customerEmail !==
          this.originalCustomerData.customerEmail ||
        this.editForm.serviceLocation !==
          this.originalCustomerData.serviceLocation
      );
    },

    // Image upload
    triggerPhotoUpload(type) {
      this.photoUpload.currentType = type;
      this.$refs.photoInput.click();
    },

    // Enhanced file input handling for mobile
    async handlePhotoSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      // More generous file size limit for mobile (up to 20MB)
      const maxSize = this.isMobileDevice() ? 20 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeLimit = this.isMobileDevice() ? '20MB' : '10MB';
        alert(`Image file size must be less than ${sizeLimit}.`);
        return;
      }

      // Add small delay for mobile devices to ensure file is ready
      if (this.isMobileDevice()) {
        setTimeout(() => {
          this.uploadPhoto(file, this.photoUpload.currentType);
        }, 100);
      } else {
        this.uploadPhoto(file, this.photoUpload.currentType);
      }
      
      // Clear the input
      event.target.value = '';
    },

    // Vue 3 compatible - Ensure photo array exists with proper reactivity
    ensurePhotoArrayExists(type) {
      if (!this.selectedJob.photos) {
        this.selectedJob.photos = {};
      }
      if (!this.selectedJob.photos[type]) {
        this.selectedJob.photos[type] = [];
      }
    },

    // Enhanced photo upload with mobile device handling (Vue 3 compatible)
    async uploadPhoto(file, type) {
      if (!this.selectedJob || !file) return;

      this.photoUpload.uploading = true;
      this.photoUpload.progress = 0;

      try {
        // Process image for mobile compatibility
        const processedFile = await this.processImageFile(file);

        const timestamp = Date.now();
        const fileExtension = processedFile.name.split('.').pop();
        const fileName = `${this.selectedJob.id}_${type}_${timestamp}.${fileExtension}`;
        const storagePath = `job-photos/${this.selectedJob.id}/${type}/${fileName}`;

        const storageRef = firebase.storage().ref(storagePath);
        const uploadTask = storageRef.put(processedFile);

        uploadTask.on('state_changed', 
          (snapshot) => {
            this.photoUpload.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => {
            console.error('Upload error:', error);
            alert('Error uploading photo. Please try again.');
            this.photoUpload.uploading = false;
          },
          async () => {
            try {
              const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();

              const photoData = {
                url: downloadURL,
                fileName: fileName,
                uploadedAt: timestamp,
                uploadedBy: firebase.auth().currentUser.uid,
                fileSize: processedFile.size,
                fileType: processedFile.type,
                isMobile: this.isMobileDevice()
              };

              // Ensure photo array exists BEFORE getting current photos
              this.ensurePhotoArrayExists(type);

              // Appends under jobPhotos/{jobId}, and hands back the updated
              // list so local state matches what was stored.
              this.selectedJob.photos[type] = await addJobPhoto(
                this.selectedJob.id,
                type,
                photoData
              );

              this.photoUpload.uploading = false;
              this.photoUpload.progress = 0;

              // Only refresh after longer delay and don't rely on it for immediate UI updates
              const refreshDelay = this.isMobileDevice() ? 3000 : 2000;
              setTimeout(async () => {
                try {
                  await this.fetchUserData();
                } catch (refreshError) {
                  console.error('Error refreshing data:', refreshError);
                }
              }, refreshDelay);

            } catch (error) {
              console.error('Error saving photo data:', error);
              alert('Error saving photo information. Please try again.');
              this.photoUpload.uploading = false;
            }
          }
        );

      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Error uploading photo. Please try again.');
        this.photoUpload.uploading = false;
      }
    },

    // Process image file for mobile compatibility
    // Job photos go to Facebook and Google in Phase 4, and neither accepts
    // HEIC - nor can a non-Safari browser display it. Normalise on upload.
    async processImageFile(file) {
      return prepareImageForUpload(file, { maxWidth: 2400, maxHeight: 2400, quality: 0.92 });
    },

    // Resize image if too large (helpful for mobile)
    resizeImage(file, maxWidth, maxHeight) {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw resized image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert back to blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Create new file with original name and type
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          }, file.type, 0.9); // 90% quality
        };
        
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    },

    // Detect mobile device
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    },

    async deletePhoto(type, index) {
      if (!this.selectedJob?.photos?.[type]?.[index]) return;

      const photo = this.selectedJob.photos[type][index];
      const confirmDelete = confirm('Are you sure you want to delete this photo?');
      if (!confirmDelete) return;

      this.photoUpload.deleting = true;

      try {
        // Delete from storage
        const storageRef = firebase.storage().refFromURL(photo.url);
        await storageRef.delete();

        // Remove from local array first for immediate UI update
        // Local state is replaced from the stored result below.

        // Rebuild the entire array in Firebase (simpler than managing indices)
        this.selectedJob.photos[type] = await removeJobPhoto(
          this.selectedJob.id,
          type,
          index
        );

      } catch (error) {
        console.error('Error deleting photo:', error);
        alert('Error deleting photo. Please try again.');
        // Revert local change if database update failed
        this.fetchUserData();
      } finally {
        this.photoUpload.deleting = false;
      }
    },

    openPhotoModal(url, type, index) {
      const photo = this.selectedJob.photos[type][index];

      this.photoModal = {
        url: url,
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Photo ${
          index + 1
        }`,
        date: this.formatPhotoDate(photo.uploadedAt),
        type: type,
        index: index
      };

      const modal = new bootstrap.Modal(
        document.getElementById("photoViewerModal")
      );
      modal.show();
    },

    async deletePhotoFromModal() {
      const modal = bootstrap.Modal.getInstance(
        document.getElementById("photoViewerModal")
      );
      modal.hide();

      await this.deletePhoto(this.photoModal.type, this.photoModal.index);
    },

    formatPhotoDate(timestamp) {
      if (!timestamp || timestamp === null) return 'Just uploaded';
      
      const date = new Date(timestamp);
      
      if (isNaN(date.getTime())) return 'Just uploaded';
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },

    // Helper method to get total photo count
    getPhotoCount(type) {
      return this.selectedJob?.photos?.[type]?.length || 0;
    },

    // Helper method to check if job has any photos
    hasPhotos() {
      const beforeCount = this.getPhotoCount("before");
      const afterCount = this.getPhotoCount("after");
      return beforeCount > 0 || afterCount > 0;
    },

    // Get count of pending jobs
    getPendingJobsCount() {
      if (!this.userJobs) return 0;
      return this.userJobs.filter(
        (job) => this.determineJobStatus(job) === "pending"
      ).length;
    },

    // Get count of scheduled jobs
    getScheduledJobsCount() {
      if (!this.userJobs) return 0;
      return this.userJobs.filter(
        (job) => this.determineJobStatus(job) === "scheduled"
      ).length;
    },

    // Get recent activity (simplified version)
    getRecentActivity() {
      if (!this.userJobs) return [];

      const activities = [];

      // Get most recent 3 jobs
      const recentJobs = [...this.userJobs].slice(0, 3);

      recentJobs.forEach((job) => {
        activities.push({
          id: `job-${job.id}`,
          icon: "bi-plus-circle",
          description: `Job submitted for ${job.customerName}`,
          time: this.formatDate(job.dateSubmitted)
        });
      });

      return activities;
    },

    // Check if photos exist (already have getPhotoCount)
    hasPhotos(type) {
      return this.getPhotoCount(type) > 0;
    },

    // Get photos array (already implemented but may need this alias)
    getPhotos(type) {
      return this.selectedJob?.photos?.[type] || [];
    },

    // Refresh map placeholder
    refreshMap() {
      // Placeholder - implement based on your jobs-map component
      console.log("Map refresh triggered");
    },

    hasInvoiceSent(job) {
      return job.invoices && Object.keys(job.invoices).length > 0;
    },

    getLatestInvoiceDate(job) {
      if (!job.invoices) return null;

      const invoices = Object.values(job.invoices);
      const latestInvoice = invoices.sort((a, b) => b.sentDate - a.sentDate)[0];
      // Returns null rather than an Invalid Date: invoices with no sentDate
      // otherwise rendered the literal string "Invalid Date" into the job list.
      return latestInvoice ? parseLocalDate(latestInvoice.sentDate) : null;
    },

    formatInvoiceDate(job) {
      const date = this.getLatestInvoiceDate(job);
      return date
        ? date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric"
          })
        : "";
    },
    

      },

  async mounted() {
    await this.fetchUserData();
    // After the profile, so it never delays the dashboard rendering.
    this.fetchReviewStats();
    if (this.userJobs.length) {
      this.$nextTick(() => {
        this.initChart();
      });
    }

    // this.$on('beforeDestroy', () => {
    //   if (this.jobModal) {
    //     this.jobModal.dispose();
    //   }
    // });

    // this.initializeJobStatuses();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },

  watch: {
    // Update filtered results when filters change
    selectedStatus() {
      this.$emit("filter-changed", this.filterStats);
    },
    selectedService() {
      this.$emit("filter-changed", this.filterStats);
    },
    userJobs: {
      deep: true,
      handler(newJobs) {
        newJobs.forEach((job) => {
          const currentStatus = this.determineJobStatus(job);
          if (job.status !== currentStatus) {
            // Update job status in database if it doesn't match determined status
            this.updateJobStatus(job.id, currentStatus);
          }
        });
      }
    }
  },
  template: "#user-profile"
};
